import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  TableContainer,
  Typography,
  Chip,
  InputAdornment,
  TextField,
  alpha,
  Tooltip,
  Avatar,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Divider,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import * as actions from "./actions";
import moment from "moment";
import { useHistory, useLocation } from "react-router-dom";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import FilterListIcon from '@mui/icons-material/FilterList';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import Pagination from '@mui/material/Pagination';
import _ from "lodash";

// Styled Components
const PageHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  flexWrap: 'wrap',
  gap: theme.spacing(2),
}));

const PageTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: '1.5rem',
  color: '#F1F5F9',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
});

const StatsChip = styled(Chip)({
  background: 'linear-gradient(135deg, rgba(18, 180, 161, 0.15) 0%, rgba(5, 150, 179, 0.15) 100%)',
  border: '1px solid rgba(18, 180, 161, 0.3)',
  color: '#12B4A1',
  fontWeight: 600,
  fontSize: '0.875rem',
  height: 32,
  '& .MuiChip-label': {
    padding: '0 12px',
  },
});

const SearchField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(15, 23, 42, 0.5)',
    borderRadius: 12,
    color: '#F1F5F9',
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.1)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(18, 180, 161, 0.5)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#12B4A1',
      borderWidth: 2,
    },
  },
  '& .MuiInputAdornment-root': {
    color: '#64748B',
  },
  '& input::placeholder': {
    color: '#64748B',
    opacity: 1,
  },
});

const GlassCard = styled(Paper)({
  borderRadius: 16,
  background: 'rgba(30, 41, 59, 0.5)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  overflow: 'hidden',
});

const StyledTableContainer = styled(TableContainer)({
  background: 'transparent',
  boxShadow: 'none',
  border: 'none',
  '&.MuiPaper-root': {
    backgroundColor: 'transparent',
    backgroundImage: 'none',
  },
});

const StyledTableHead = styled(TableHead)({
  '& .MuiTableCell-root': {
    backgroundColor: 'rgba(15, 23, 42, 0.6)',
    color: '#94A3B8',
    fontWeight: 600,
    fontSize: '0.75rem',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
    padding: '14px 16px',
    whiteSpace: 'nowrap',
  },
});

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(18, 180, 161, 0.05)',
  },
  '& .MuiTableCell-root': {
    borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
    color: '#E2E8F0',
    padding: '12px 16px',
  },
}));

const VideoThumbnail = styled(Avatar)({
  width: 50,
  height: 50,
  borderRadius: 8,
  backgroundColor: 'rgba(18, 180, 161, 0.1)',
  cursor: 'pointer',
  transition: 'transform 0.2s ease',
  '& svg': {
    fontSize: 24,
    color: alpha('#12B4A1', 0.5),
  },
});

const ThumbnailPreview = styled('img')({
  width: 200,
  height: 200,
  objectFit: 'cover',
  borderRadius: 8,
  border: '2px solid rgba(18, 180, 161, 0.3)',
});

const StatusChip = styled(Chip)(({ status }) => {
  const statusStyles = {
    in_review: {
      bg: 'rgba(255, 167, 38, 0.12)',
      color: '#FFA726',
      border: 'rgba(255, 167, 38, 0.3)',
    },
    approved: {
      bg: 'rgba(102, 187, 106, 0.12)',
      color: '#66BB6A',
      border: 'rgba(102, 187, 106, 0.3)',
    },
    rejected: {
      bg: 'rgba(255, 107, 107, 0.12)',
      color: '#FF6B6B',
      border: 'rgba(255, 107, 107, 0.3)',
    },
    takendown: {
      bg: 'rgba(255, 107, 107, 0.12)',
      color: '#FF6B6B',
      border: 'rgba(255, 107, 107, 0.3)',
    },
  };
  const style = statusStyles[status] || statusStyles.in_review;
  return {
    backgroundColor: style.bg,
    color: style.color,
    border: `1px solid ${style.border}`,
    fontWeight: 600,
    fontSize: '0.7rem',
    height: 26,
    '& .MuiChip-icon': {
      color: style.color,
      fontSize: 14,
    },
  };
});

const EmptyState = styled(Box)({
  textAlign: 'center',
  padding: '48px 24px',
  color: '#64748B',
});

const StatusFilterSelect = styled(Select)({
  backgroundColor: 'rgba(15, 23, 42, 0.5)',
  borderRadius: 12,
  color: '#F1F5F9',
  minWidth: 220,
  height: 40,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(18, 180, 161, 0.5)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#12B4A1',
    borderWidth: 2,
  },
  '& .MuiSelect-icon': {
    color: '#64748B',
  },
  '& .MuiSelect-select': {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 14px',
  },
});

const StatusMenuItem = styled(MenuItem)({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  '&.Mui-selected': {
    backgroundColor: 'rgba(18, 180, 161, 0.1)',
  },
  '&:hover': {
    backgroundColor: 'rgba(18, 180, 161, 0.05)',
  },
});

const ViewButton = styled(IconButton)({
  backgroundColor: 'rgba(18, 180, 161, 0.1)',
  color: '#12B4A1',
  '&:hover': {
    backgroundColor: 'rgba(18, 180, 161, 0.2)',
  },
});

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    backgroundColor: '#1E293B',
    backgroundImage: 'none',
    borderRadius: 16,
    border: '1px solid rgba(255, 255, 255, 0.08)',
    maxWidth: 700,
    width: '100%',
  },
});

const DialogHeader = styled(DialogTitle)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: 'rgba(15, 23, 42, 0.6)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
  padding: '16px 24px',
  color: '#F1F5F9',
});

const DetailRow = styled(Box)({
  display: 'flex',
  padding: '12px 0',
  borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
  '&:last-child': {
    borderBottom: 'none',
  },
});

const DetailLabel = styled(Typography)({
  color: '#94A3B8',
  fontWeight: 600,
  fontSize: '0.875rem',
  minWidth: 140,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
});

const DetailValue = styled(Typography)({
  color: '#E2E8F0',
  fontSize: '0.875rem',
  flex: 1,
  wordBreak: 'break-word',
});

const statusOptions = [
  {
    value: 'all',
    label: 'All Statuses',
    icon: <FilterListIcon sx={{ fontSize: 20, color: '#12B4A1' }} />
  },
  {
    value: 'in_review',
    label: 'In Review',
    icon: <AccessTimeIcon sx={{ fontSize: 20, color: '#FFA726' }} />
  },
  {
    value: 'approved',
    label: 'Approved',
    icon: <CheckCircleIcon sx={{ fontSize: 20, color: '#66BB6A' }} />
  },
  {
    value: 'rejected',
    label: 'Rejected',
    icon: <CancelIcon sx={{ fontSize: 20, color: '#FF6B6B' }} />
  },
  {
    value: 'takendown',
    label: 'Takendown',
    icon: <RemoveCircleOutlineIcon sx={{ fontSize: 20, color: '#FF6B6B' }} />
  },
];

export default function VideoCatalog(props) {
  const history = useHistory();
  const location = useLocation();

  const [selectedStatus, setSelectedStatus] = useState('all');
  const [releases, setReleases] = useState([]);
  const [filters, setFilters] = useState({
    sort: 'all',
    limit: 50,
    offset: 0,
    page: 1,
  });
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState('');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  // Read URL parameter on mount and when URL changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusParam = params.get('status') || 'all';
    const validStatuses = ['in_review', 'approved', 'rejected', 'takendown', 'all'];

    if (validStatuses.includes(statusParam)) {
      setSelectedStatus(statusParam);
      setFilters(prev => ({ ...prev, sort: statusParam, page: 1, offset: 0 }));
    } else {
      history.replace('/video-catalog?status=all');
    }
  }, [location.search]);

  const onGetVideoReleases = useCallback((filters) => {
    props.setLoader(true);
    actions.getVideoCatalogListAdmin(filters).then((res) => {
      props.setLoader(false);
      setReleases(res.body);
      setCount(res.total);
    }).catch(() => {
      props.setLoader(false);
    });
  }, []);

  const getSearchReleases = useCallback((searchFilters) => {
    props.setLoader(true);
    actions.searchInVideoReleases(searchFilters).then((res) => {
      props.setLoader(false);
      setReleases(res.body);
      setCount(res.total || 0);
    }).catch(() => {
      props.setLoader(false);
    });
  }, []);

  const delaySearch = useMemo(
    () => _.debounce((searchFilters) => getSearchReleases(searchFilters), 500),
    [getSearchReleases]
  );

  useEffect(() => {
    if (!search) {
      onGetVideoReleases(filters);
    } else {
      getSearchReleases({
        search,
        type: filters.sort,
        limit: filters.limit,
        offset: filters.offset
      });
    }
  }, [filters.sort, filters.limit, filters.offset, filters.page]);

  const handleStatusChange = (newStatus) => {
    setSelectedStatus(newStatus);
    history.push(`/video-catalog?status=${newStatus}`);
    setFilters({ ...filters, sort: newStatus, page: 1, offset: 0 });
    setSearch('');
  };

  const onPageChange = (event, page) => {
    const offset = (page - 1) * filters.limit;
    setFilters({ ...filters, page, offset });
  };

  const searchReleases = (value) => {
    setSearch(value);
    if (!value) {
      onGetVideoReleases(filters);
    } else {
      delaySearch({
        search: value,
        type: filters.sort,
        limit: filters.limit,
        offset: 0
      });
      setFilters(prev => ({ ...prev, page: 1, offset: 0 }));
    }
  };

  const handleViewDetails = (video) => {
    setSelectedVideo(video);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedVideo(null);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircleIcon />;
      case 'rejected':
        return <CancelIcon />;
      case 'takendown':
        return <RemoveCircleOutlineIcon />;
      default:
        return <AccessTimeIcon />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      case 'takendown':
        return 'Takendown';
      default:
        return 'In Review';
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    if (!isNaN(Number(dateValue))) {
      return moment(Number(dateValue)).format('MMM DD, YYYY');
    }
    return dateValue;
  };

  let totalPages = Math.ceil(Number(count) / filters.limit);

  return (
    <Box>
      <Helmet>
        <title>Video Catalog - Fronic Media Labels</title>
      </Helmet>
      <PageHeader>
        <PageTitle>
          <VideoLibraryIcon sx={{ color: '#12B4A1', fontSize: 28 }} />
          Video Catalog
        </PageTitle>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <StatsChip label={`Total: ${count}`} />
          <StatusFilterSelect
            value={selectedStatus}
            onChange={(e) => handleStatusChange(e.target.value)}
            size="small"
            aria-label="Filter by video release status"
          >
            {statusOptions.map((option) => (
              <StatusMenuItem key={option.value} value={option.value}>
                {option.icon}
                <Typography sx={{ fontSize: '0.875rem' }}>{option.label}</Typography>
              </StatusMenuItem>
            ))}
          </StatusFilterSelect>
        </Box>
      </PageHeader>

      <Box sx={{ mb: 3 }}>
        <SearchField
          size="small"
          placeholder="Search by title or user..."
          value={search}
          onChange={(e) => searchReleases(e.target.value)}
          sx={{ width: '50%' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <GlassCard elevation={0}>
        <StyledTableContainer component={Paper} elevation={0}>
          <Table>
            <StyledTableHead>
              <TableRow>
                <TableCell sx={{ width: 60 }}>#</TableCell>
                <TableCell>Thumbnail</TableCell>
                <TableCell>Release Title</TableCell>
                <TableCell>Audio Title</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Release Date</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">View Details</TableCell>
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {releases.length > 0 ? (
                releases.map((item, key) => (
                  <StyledTableRow key={key}>
                    <TableCell>
                      <Typography sx={{ color: '#64748B', fontSize: '0.875rem' }}>
                        {(filters.page - 1) * filters.limit + (key + 1)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip
                        title={
                          item.thumbnail ? (
                            <ThumbnailPreview src={item.thumbnail} alt="Video thumbnail" />
                          ) : (
                            <Box
                              sx={{
                                width: 200,
                                height: 200,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(15, 23, 42, 0.8)',
                                borderRadius: 2,
                                border: '2px dashed rgba(100, 116, 139, 0.5)',
                              }}
                            >
                              <Typography sx={{ color: '#64748B', fontSize: '0.875rem' }}>
                                IMAGE not available
                              </Typography>
                            </Box>
                          )
                        }
                        placement="right"
                        arrow
                        componentsProps={{
                          tooltip: {
                            sx: {
                              backgroundColor: '#1E293B',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              borderRadius: 2,
                              p: 0.5,
                            },
                          },
                          arrow: {
                            sx: {
                              color: '#1E293B',
                            },
                          },
                        }}
                      >
                        <VideoThumbnail
                          src={item.thumbnail}
                          variant="rounded"
                        >
                          <VideoLibraryIcon />
                        </VideoThumbnail>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={item.release_title || '-'} placement="top">
                        <Typography sx={{ color: '#F1F5F9', fontWeight: 500 }}>
                          {item.release_title?.length > 30
                            ? `${item.release_title.substring(0, 30)}...`
                            : item.release_title || '-'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={item.audio_title || '-'} placement="top">
                        <Typography sx={{ color: '#94A3B8', fontSize: '0.875rem' }}>
                          {item.audio_title?.length > 30
                            ? `${item.audio_title.substring(0, 30)}...`
                            : item.audio_title || '-'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 500, color: '#E2E8F0' }}>
                        {item.name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: '#94A3B8', fontSize: '0.875rem' }}>
                        {formatDate(item.release_date) !== '-' ? formatDate(item.release_date) : item.date || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <StatusChip
                        icon={getStatusIcon(item.status)}
                        label={getStatusLabel(item.status)}
                        status={item.status}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details" placement="top">
                        <ViewButton
                          size="small"
                          onClick={() => handleViewDetails(item)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </ViewButton>
                      </Tooltip>
                    </TableCell>
                  </StyledTableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8}>
                    <EmptyState>
                      <VideoLibraryIcon sx={{ fontSize: 48, color: '#334155', mb: 2 }} />
                      <Typography variant="body1" sx={{ color: '#64748B' }}>
                        No video releases found
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#475569', mt: 1 }}>
                        Try adjusting your search criteria or filter
                      </Typography>
                    </EmptyState>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>
        {totalPages > 1 && (
          <Box sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.06)' }}>
            <Pagination
              count={totalPages}
              showFirstButton
              showLastButton
              onChange={onPageChange}
              page={filters.page || 1}
            />
          </Box>
        )}
      </GlassCard>

      {/* View Details Dialog */}
      <StyledDialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogHeader>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VideoLibraryIcon sx={{ color: '#12B4A1' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Video Release Details
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDetails} sx={{ color: '#94A3B8' }}>
            <CloseIcon />
          </IconButton>
        </DialogHeader>
        <DialogContent sx={{ p: 3 }}>
          {selectedVideo && (
            <Grid container spacing={3}>
              {/* Details Grid */}
              <Grid item xs={12} md={6}>
                <DetailRow>
                  <DetailLabel>Video ID</DetailLabel>
                  <DetailValue>{selectedVideo.video_id || '-'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>User ID</DetailLabel>
                  <DetailValue>{selectedVideo.user_id || '-'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>User Name</DetailLabel>
                  <DetailValue>{selectedVideo.name || '-'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Email</DetailLabel>
                  <DetailValue>{selectedVideo.email || '-'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Release ID</DetailLabel>
                  <DetailValue>{selectedVideo.release_id || '-'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Release Title</DetailLabel>
                  <DetailValue>{selectedVideo.release_title || '-'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Audio Title</DetailLabel>
                  <DetailValue>{selectedVideo.audio_title || '-'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Actor</DetailLabel>
                  <DetailValue>{selectedVideo.actor || '-'}</DetailValue>
                </DetailRow>
              </Grid>

              <Grid item xs={12} md={6}>
                <DetailRow>
                  <DetailLabel>Actress</DetailLabel>
                  <DetailValue>{selectedVideo.actress || '-'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Video ISRC</DetailLabel>
                  <DetailValue>{selectedVideo.video_isrc || '-'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Video UPC</DetailLabel>
                  <DetailValue>{selectedVideo.video_upc || '-'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Release Date</DetailLabel>
                  <DetailValue>{formatDate(selectedVideo.release_date) !== '-' ? formatDate(selectedVideo.release_date) : selectedVideo.date || '-'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Status</DetailLabel>
                  <DetailValue>
                    <StatusChip
                      icon={getStatusIcon(selectedVideo.status)}
                      label={getStatusLabel(selectedVideo.status)}
                      status={selectedVideo.status}
                      size="small"
                    />
                  </DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Reason</DetailLabel>
                  <DetailValue>{selectedVideo.reason || '-'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Created At</DetailLabel>
                  <DetailValue>
                    {selectedVideo.created_at
                      ? moment(selectedVideo.created_at).format('MMM DD, YYYY hh:mm A')
                      : '-'}
                  </DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Date</DetailLabel>
                  <DetailValue>{selectedVideo.date || '-'}</DetailValue>
                </DetailRow>
              </Grid>

              {/* Download Link - Full Width */}
              <Grid item xs={12}>
                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)', my: 1 }} />
                <DetailRow>
                  <DetailLabel>Download Link</DetailLabel>
                  <DetailValue>
                    {selectedVideo.download_link ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinkIcon sx={{ color: '#12B4A1', fontSize: 18 }} />
                        <a
                          href={selectedVideo.download_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#12B4A1', textDecoration: 'none' }}
                        >
                          {selectedVideo.download_link.length > 50
                            ? `${selectedVideo.download_link.substring(0, 50)}...`
                            : selectedVideo.download_link}
                        </a>
                      </Box>
                    ) : '-'}
                  </DetailValue>
                </DetailRow>
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </StyledDialog>
    </Box>
  );
}
